import { createWithEqualityFn } from 'zustand/traditional';
import { fetchSelfTasks, type TaskSelfItem } from '../api/tasksSelf';
import { refreshUserSelf } from './userSelfStore';
import { type Task } from '../hooks/useTaskManager';
import { prefetchTaskMediaFromRecords } from '../utils/taskMediaCache';
import {
  normalizeTaskStatus,
  parseTaskProgress,
  normalizeTimestamp,
  pickEarliestTimestamp,
  pickLatestTimestamp,
} from '../utils/taskPollingUtils';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;


const extractPromptText = (item: TaskSelfItem): string | undefined => {
  const pickString = (value: unknown): string | undefined => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
    return undefined;
  };

  const propertiesInput = item.properties?.input;
  if (propertiesInput) {
    const fromProperties = pickString(propertiesInput);
    if (fromProperties) return fromProperties;
    if (
      typeof propertiesInput === 'object' &&
      propertiesInput !== null &&
      'prompt' in (propertiesInput as Record<string, unknown>)
    ) {
      const prompt = pickString((propertiesInput as Record<string, unknown>).prompt);
      if (prompt) return prompt;
    }
  }

  const detail = item.data?.detail;
  if (detail && typeof detail === 'object') {
    const prompt = pickString((detail as Record<string, unknown>).prompt);
    if (prompt) return prompt;
    const input = (detail as Record<string, unknown>).input;
    if (input && typeof input === 'object') {
      const nestedPrompt = pickString((input as Record<string, unknown>).prompt);
      if (nestedPrompt) return nestedPrompt;
    }
  }

  return undefined;
};

const normalizeErrorReasonLabel = (reason: string): string => {
  const normalized = reason.trim().toLowerCase();
  switch (normalized) {
    case 'output_moderation':
      return '输出内容未通过审核 (output_moderation)';
    case 'input_moderation':
      return '输入内容未通过审核 (input_moderation)';
    case 'rate_limited':
      return '请求过于频繁 (rate_limited)';
    default:
      return reason;
  }
};

const extractErrorMessage = (item: TaskSelfItem): string | undefined => {
  const seen = new Set<string>();
  const addMessage = (value: unknown, { normalizeReason } = { normalizeReason: false }) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed) return;
    const message = normalizeReason ? normalizeErrorReasonLabel(trimmed) : trimmed;
    if (seen.has(message)) return;
    seen.add(message);
  };

  // 1) 顶层 fail_reason
  addMessage(item.fail_reason);

  // 2) data 层错误聚合
  const data = item.data;
  if (isRecord(data)) {
    const record = data as Record<string, unknown>;

    // 2.1 顶层常见错误字段
    addMessage(record['message']);
    addMessage(record['error_message']);
    addMessage(record['error_msg']);
    addMessage(record['fail_reason']);
    addMessage(record['error_reason'], { normalizeReason: true });

    // 2.2 error 既可能是字符串，也可能是对象
    const topError = record['error'];
    if (typeof topError === 'string') {
      addMessage(topError);
    } else if (isRecord(topError)) {
      const errObj = topError as Record<string, unknown>;
      addMessage(errObj['message']);
      addMessage(errObj['msg']);
      addMessage(errObj['detail']);
      addMessage(errObj['error_message']);
      addMessage(errObj['fail_reason']);
      addMessage(errObj['error_reason'], { normalizeReason: true });

      // error.detail 可能继续嵌套
      const innerDetail = errObj['detail'];
      if (isRecord(innerDetail)) {
        const d = innerDetail as Record<string, unknown>;
        addMessage(d['message']);
        addMessage(d['msg']);
        addMessage(d['error']);
        addMessage(d['error_message']);
        addMessage(d['fail_reason']);
        addMessage(d['error_reason'], { normalizeReason: true });
      }
    }

    // 2.3 data.detail
    const detail = isRecord(record['detail']) ? (record['detail'] as Record<string, unknown>) : undefined;
    if (detail) {
      addMessage(detail['message']);
      addMessage(detail['msg']);
      addMessage(detail['error']);
      addMessage(detail['error_message']);
      addMessage(detail['fail_reason']);
      addMessage(detail['error_reason'], { normalizeReason: true });

      // 2.3.1 detail.draft_info
      const draftInfo = isRecord(detail['draft_info']) ? (detail['draft_info'] as Record<string, unknown>) : undefined;
      if (draftInfo) {
        addMessage(draftInfo['message']);
        addMessage(draftInfo['msg']);
        addMessage(draftInfo['error']);
        addMessage(draftInfo['error_message']);
        addMessage(draftInfo['fail_reason']);
        addMessage(draftInfo['error_reason'], { normalizeReason: true });
      }
    }
  }

  if (seen.size === 0) {
    return undefined;
  }
  return Array.from(seen).join('；');
};

export const mapTaskSelfItemToTask = (item: TaskSelfItem): Task => {
  const detail = isRecord(item.data?.detail) ? (item.data!.detail as Record<string, unknown>) : undefined;
  const pendingInfo = isRecord(detail?.pending_info) ? (detail!.pending_info as Record<string, unknown>) : undefined;
  const statusUpdateAt = normalizeTimestamp(
    isRecord(item.data) ? (item.data as Record<string, unknown>).status_update_time : undefined
  );

  const submittedAt =
    normalizeTimestamp(item.submit_time) ??
    normalizeTimestamp(item.created_at) ??
    Date.now();

  const createdAt = normalizeTimestamp(item.created_at) ?? submittedAt;

  const startedAtCandidates: Array<number | undefined> = [
    normalizeTimestamp(item.start_time),
    normalizeTimestamp(detail?.started_at),
    normalizeTimestamp(detail?.start_time),
    normalizeTimestamp(detail?.created_at),
    normalizeTimestamp(pendingInfo?.created_at),
    submittedAt,
  ];
  const startedAt = pickEarliestTimestamp(startedAtCandidates) ?? submittedAt;

  const finishedAtCandidates: Array<number | undefined> = [
    normalizeTimestamp(item.finish_time),
    normalizeTimestamp(detail?.finished_at),
    normalizeTimestamp(detail?.completed_at),
    statusUpdateAt,
    normalizeTimestamp(item.updated_at),
  ];
  const finishedAt = pickLatestTimestamp(finishedAtCandidates);

  const updatedAtCandidates: Array<number | undefined> = [
    finishedAt,
    statusUpdateAt,
    normalizeTimestamp(item.updated_at),
    startedAt,
    createdAt,
  ];
  const updatedAt = pickLatestTimestamp(updatedAtCandidates) ?? Date.now();

  // 优先使用后端返回的 data.model 作为模型标识（如 'sora-2-async'），
  // 便于下游 Sora 相关逻辑判定以展示 Remix 按钮。
  const dataModel =
    isRecord(item.data) && typeof (item.data as Record<string, unknown>).model === 'string'
      ? ((item.data as Record<string, unknown>).model as string).trim()
      : '';
  const modelLabelCandidates: Array<unknown> = [dataModel, item.platform, item.action];
  const modelLabel =
    modelLabelCandidates
      .map((candidate) => (typeof candidate === 'string' ? candidate.trim() : ''))
      .find((candidate) => candidate.length > 0) ?? '任务';

  return {
    id: item.task_id || String(item.id),
    requestId: item.task_id,
    status: normalizeTaskStatus(item.status),
    model: modelLabel,
    prompt: extractPromptText(item),
    payload: undefined,
    createdAt,
    updatedAt,
    submittedAt,
    startedAt,
    finishedAt,
    result: (item.data as Task['result']) ?? null,
    error: extractErrorMessage(item),
    progress: parseTaskProgress(item.progress),
    statusUrl: undefined,
  };
};

export interface TaskSelfRecord {
  task: Task;
  raw: TaskSelfItem;
}

export interface TaskSelfStoreState {
  records: TaskSelfRecord[];
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  total: number;
  timeRange: {
    start: number | null;
    end: number | null;
  };
  resolvedTimeRange: {
    start: number | null;
    end: number | null;
  };
  selectedTaskId: string | null;
  fetch: () => Promise<void>;
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setTimeRange: (range: { start: number | null; end: number | null }) => void;
  selectTask: (taskId: string | null) => void;
  /**
   * 加载更多更旧的任务（下一页），将结果追加到现有记录前部（因为列表升序展示，追加旧任务不会改变最新任务位置）。
  */
  fetchMoreOlder: () => Promise<void>;
}

export const useTaskSelfStore = createWithEqualityFn<TaskSelfStoreState>()((set, get) => ({
  records: [],
  loading: false,
  error: null,
  page: 1,
  // 默认 30 条，满足“生命周期开始时，尝试获取最近30条任务”
  pageSize: 30,
  total: 0,
  // 取消默认“今天”时间范围限制，按最近任务分页返回
  timeRange: {
    start: null,
    end: null,
  },
  resolvedTimeRange: {
    start: null,
    end: null,
  },
  selectedTaskId: null,

  async fetch() {
    const state = get();

    set({ loading: true, error: null });

    try {
      const response = await fetchSelfTasks({
        page: state.page,
        pageSize: state.pageSize,
        startTimestamp: state.timeRange.start ?? undefined,
        endTimestamp: state.timeRange.end ?? undefined,
      });

      if (!response.success) {
        throw new Error(response.message || '任务列表获取失败');
      }

      if (!response.data) {
        throw new Error('任务列表返回数据为空');
      }

      const { items, total, page, page_size: pageSize, start_timestamp, end_timestamp } = response.data;
      const records = Array.isArray(items)
        ? items.map((item) => ({
            task: mapTaskSelfItemToTask(item),
            raw: item,
          }))
        : [];

      const nextTotal = typeof total === 'number' ? total : records.length;
      const nextPage = typeof page === 'number' && page > 0 ? page : state.page;
      const nextPageSize = typeof pageSize === 'number' && pageSize > 0 ? pageSize : state.pageSize;
      const resolvedTimeRange = {
        start: typeof start_timestamp === 'number' && start_timestamp > 0 ? start_timestamp : null,
        end: typeof end_timestamp === 'number' && end_timestamp > 0 ? end_timestamp : null,
      };

      set({
        records,
        total: nextTotal,
        page: nextPage,
        pageSize: nextPageSize,
        loading: false,
        error: null,
        resolvedTimeRange,
      });

      // 预缓存成功任务的媒体内容（图片/视频），避免短期链接过期
      void prefetchTaskMediaFromRecords(records);
    } catch (error) {
      const message = error instanceof Error ? error.message : '任务列表请求异常';
      set({
        loading: false,
        error: message,
      });
    }
  },

  async refetch() {
    const state = get();
    // 始终刷新最新一页（page=1），并与现有记录合并，避免覆盖已加载的旧记录
    try {
      const response = await fetchSelfTasks({
        page: 1,
        pageSize: state.pageSize,
        startTimestamp: state.timeRange.start ?? undefined,
        endTimestamp: state.timeRange.end ?? undefined,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || '最新任务刷新失败');
      }

      const { items, total, page_size: respPageSize, start_timestamp, end_timestamp } = response.data;
      const latestRecords = Array.isArray(items)
        ? items.map((item) => ({ task: mapTaskSelfItemToTask(item), raw: item }))
        : [];

      const seen = new Set<string>();
      // 用最新页覆盖同 id 的旧条目，然后保留其余既有条目
      const merged: TaskSelfRecord[] = [];
      for (const rec of latestRecords) {
        merged.push(rec);
        seen.add(rec.task.id);
      }
      for (const rec of state.records) {
        if (!seen.has(rec.task.id)) {
          merged.push(rec);
          seen.add(rec.task.id);
        }
      }

      const nextTotal = typeof total === 'number' ? total : Math.max(state.total, merged.length);
      const nextPageSize =
        typeof respPageSize === 'number' && respPageSize > 0 ? respPageSize : state.pageSize;
      const resolvedTimeRange = {
        start: typeof start_timestamp === 'number' && start_timestamp > 0 ? start_timestamp : null,
        end: typeof end_timestamp === 'number' && end_timestamp > 0 ? end_timestamp : null,
      };

      set({
        records: merged,
        total: nextTotal,
        // 保持当前 page，不回退；pageSize 以服务端返回或现有为准
        pageSize: nextPageSize,
        resolvedTimeRange,
      });

      // 预缓存刷新后的任务媒体内容
      void prefetchTaskMediaFromRecords(merged);

      // 获取任务列表后刷新余额
      void refreshUserSelf();
    } catch (error) {
      // 遇错仅记录，不破坏现有 records
      const message = error instanceof Error ? error.message : '最新任务刷新异常';
      set({ error: message });
    }
  },

  setPage(page) {
    const normalized = Number.isFinite(page) && page > 0 ? Math.trunc(page) : 1;
    set(() => ({
      page: normalized,
    }));
  },

  setPageSize(size) {
    const normalized = Number.isFinite(size) && size > 0 ? Math.trunc(size) : 10;
    set({
      pageSize: normalized,
      page: 1,
    });
  },

  setTimeRange(range) {
    const nextStart = range.start ?? null;
    const nextEnd = range.end ?? null;
    set((state) => {
      const prev = state.timeRange;
      if (prev.start === nextStart && prev.end === nextEnd) {
        return state;
      }
      return {
        ...state,
        timeRange: { start: nextStart, end: nextEnd },
        page: 1,
      };
    });
  },

  selectTask(taskId) {
    set({ selectedTaskId: taskId });
  },

  async fetchMoreOlder() {
    const state = get();
    // 若已经在加载，避免并发
    if (state.loading) return;

    const nextPage = (state.page ?? 1) + 1;
    set({ loading: true, error: null });

    try {
      const response = await fetchSelfTasks({
        page: nextPage,
        pageSize: state.pageSize,
        startTimestamp: state.timeRange.start ?? undefined,
        endTimestamp: state.timeRange.end ?? undefined,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || '历史任务获取失败');
      }

      const { items, total, page: respPage, page_size: respPageSize } = response.data;
      const appendRecords = Array.isArray(items)
        ? items.map((item) => ({ task: mapTaskSelfItemToTask(item), raw: item }))
        : [];

      // 以任务 id 去重，避免重复
      const seen = new Set<string>(state.records.map((r) => r.task.id));
      const merged = [...state.records];
      for (const rec of appendRecords) {
        if (!seen.has(rec.task.id)) {
          merged.push(rec);
          seen.add(rec.task.id);
        }
      }

      const nextTotal = typeof total === 'number' ? total : Math.max(state.total, merged.length);
      const resolvedPage = typeof respPage === 'number' && respPage > 0 ? respPage : nextPage;
      const nextPageSize =
        typeof respPageSize === 'number' && respPageSize > 0 ? respPageSize : state.pageSize;

      set({
        records: merged,
        total: nextTotal,
        page: resolvedPage,
        pageSize: nextPageSize,
        loading: false,
        error: null,
      });

      // 预缓存包含更多历史任务的媒体内容
      void prefetchTaskMediaFromRecords(merged);

      // 加载更多任务后刷新余额
      void refreshUserSelf();
    } catch (error) {
      const message = error instanceof Error ? error.message : '历史任务请求异常';
      set({ loading: false, error: message });
    }
  },
}));
